import { useState } from "react";
import { SectionHead, Reveal } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";
import { ShieldCheck } from "lucide-react";
import { TcoShare } from "./TcoShare";

export const FleetEconomics = () => {
  const { t } = useLang();
  const e = t.tco;
  const [km, setKm] = useState(150);
  const [fuel, setFuel] = useState(180);
  const [fleet, setFleet] = useState(50);

  const KES_USD = 129;
  const KM_PER_L = 45;
  const ICE_MAINT = 4.63;
  const EV_ENERGY = 0.4;
  const EV_MAINT = 2.08;
  const BAAS_DAY = 425;

  const iceDay = (km / KM_PER_L) * fuel + km * ICE_MAINT;
  const evDay = km * EV_ENERGY + km * EV_MAINT + BAAS_DAY;
  const saveDay = iceDay - evDay;
  const savePct = iceDay > 0 ? (saveDay / iceDay) * 100 : 0;
  const monthFleet = saveDay * 30 * fleet;
  const fiveYrFleetUsd = (saveDay * 360 * 5 * fleet) / KES_USD;

  const fmtKES = (v) => `${Math.round(v).toLocaleString("en-US")} KES`;
  const fmtUSD = (v) => `$${Math.round(v).toLocaleString("en-US")}`;

  const sliders = [
    { id: "km", label: e.calc.kmLabel, val: km, set: setKm, min: 50, max: 300, step: 5, unit: "km" },
    { id: "fuel", label: e.calc.fuelLabel, val: fuel, set: setFuel, min: 120, max: 260, step: 5, unit: "KES/L" },
    { id: "fleet", label: e.calc.fleetLabel, val: fleet, set: setFleet, min: 1, max: 500, step: 1, unit: e.calc.fleetUnit },
  ];

  const outputs = [
    { id: "daily", val: `+${fmtKES(saveDay)}`, label: e.calc.outDaily, hot: true },
    { id: "pct", val: `${savePct.toFixed(1)}%`, label: e.calc.outPct, hot: false },
    { id: "month", val: fmtKES(monthFleet), label: e.calc.outMonth, hot: false },
    { id: "fiveyr", val: fmtUSD(fiveYrFleetUsd), label: e.calc.out5y, hot: true },
  ];
  return (
    <section id="economics" className="relative bg-sand py-24 sm:py-32 border-b border-technical" data-testid="economics-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={e.eyebrow} title={e.title} desc={e.desc} testid="economics" />

        <Reveal delay={0.1}>
          <p className="mt-14 eyebrow" data-testid="economics-integration-eyebrow">{e.integrationEyebrow}</p>
        </Reveal>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-[rgba(22,24,27,0.12)] border border-technical">
          {e.metrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.05} className="bg-white p-5 sm:p-6">
              <div className="font-display text-xl sm:text-2xl font-black tracking-tight text-ink" data-testid={`economics-metric-${i}`}>
                {m.val}
              </div>
              <div className="mt-1.5 font-mono-tech text-[9px] uppercase tracking-[0.14em] text-[#525964]">{m.label}</div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex items-start gap-3 border border-dashed border-[rgba(122,22,32,0.4)] bg-white px-5 py-4" data-testid="economics-integration-note">
            <span className="pulse-dot mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-red-brand" />
            <p className="text-xs sm:text-sm leading-relaxed text-[#525964]">{e.integrationNote}</p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow" data-testid="tco-table-eyebrow">{e.tableEyebrow}</p>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-3 font-mono-tech text-[10px] leading-relaxed tracking-wide text-[#525964] border-l-2 border-red-brand pl-4">
                {e.assumption}
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-6 overflow-x-auto border border-technical bg-white" data-testid="tco-table">
                <table className="w-full min-w-[560px] text-left">
                  <thead>
                    <tr className="bg-ink">
                      <th className="w-[36%] px-4 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]/50">—</th>
                      <th className="px-4 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]/80">{e.colA}</th>
                      <th className="px-4 py-3.5 font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#e63946]">{e.colB}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {e.rows.map((r, i) => (
                      <tr key={r[0]} data-testid={`tco-row-${i}`} className={`border-t border-technical ${i % 2 ? "bg-canvas" : "bg-white"}`}>
                        <td className="px-4 py-3.5 text-xs font-semibold text-ink">{r[0]}</td>
                        <td className="px-4 py-3.5 text-xs text-[#525964]">{r[1]}</td>
                        <td className="px-4 py-3.5 text-xs font-semibold text-red-brand">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-3 font-mono-tech text-[9px] uppercase tracking-[0.15em] text-[#9ca3af]">{e.footnote}</p>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="chamfer mt-10 border border-technical bg-ink p-6 sm:p-8" data-testid="tco-calculator">
                <p className="font-mono-tech text-[0.7rem] tracking-[0.28em] uppercase text-[#e63946]" data-testid="calc-eyebrow">
                  {e.calc.eyebrow}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-[#f8f7f4]/55">{e.calc.desc}</p>
                <div className="mt-6 space-y-5">
                  {sliders.map((s) => (
                    <div key={s.id}>
                      <div className="flex items-center justify-between gap-4">
                        <label htmlFor={`calc-slider-${s.id}`} className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]/60">
                          {s.label}
                        </label>
                        <span data-testid={`calc-${s.id}-value`} className="font-mono-tech text-sm font-bold text-[#f8f7f4]">
                          {s.val} <span className="text-[10px] font-normal text-[#f8f7f4]/50">{s.unit}</span>
                        </span>
                      </div>
                      <input
                        id={`calc-slider-${s.id}`}
                        data-testid={`calc-slider-${s.id}`}
                        type="range"
                        min={s.min}
                        max={s.max}
                        step={s.step}
                        value={s.val}
                        onChange={(ev) => s.set(Number(ev.target.value))}
                        className="mt-2.5 w-full cursor-pointer accent-[#e63946]"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-7 grid grid-cols-2 gap-px border border-[#f8f7f4]/15 bg-[#f8f7f4]/15">
                  {outputs.map((o) => (
                    <div key={o.id} data-testid={`calc-out-${o.id}`} className="bg-ink p-4 sm:p-5">
                      <div className={`font-display text-lg sm:text-2xl font-black tracking-tight ${o.hot ? "text-[#e63946]" : "text-[#f8f7f4]"}`}>
                        {o.val}
                      </div>
                      <div className="mt-1 font-mono-tech text-[9px] uppercase tracking-[0.13em] text-[#f8f7f4]/45">{o.label}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono-tech text-[9px] uppercase tracking-[0.13em] text-[#f8f7f4]/35" data-testid="calc-constants">
                  {e.calc.constants}
                </p>
                <TcoShare km={km} fuel={fuel} fleet={fleet} nums={{ daily: Math.round(saveDay).toLocaleString("en-US"), pct: savePct.toFixed(1), month: Math.round(monthFleet).toLocaleString("en-US"), five: fmtUSD(fiveYrFleetUsd) }} />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow flex items-center gap-2.5" data-testid="underwriting-eyebrow">
                <ShieldCheck className="h-4 w-4" />
                {e.uwEyebrow}
              </p>
            </Reveal>
            <div className="mt-6 space-y-4">
              {e.uwCards.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.07}>
                  <article
                    data-testid={`underwriting-card-${i}`}
                    className="group border border-technical bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(122,22,32,0.4)] hover:shadow-[0_14px_32px_-16px_rgba(22,24,27,0.3)]"
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono-tech text-[10px] font-semibold text-red-brand">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-ink">{c.title}</h3>
                    </div>
                    <p className="mt-2.5 text-xs sm:text-sm leading-relaxed text-[#525964]">{c.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
